<template>
  <v-container fluid class="pa-7">
    <header-component class="pb-4" :title="$vuetify.lang.t('$vuetify.navigation.orders')">
      <template v-if="lastChangeText" v-slot:latestUpdate>
        <v-alert
          border="left"
          color="green"
          dark
          @click="$router.push({ name: 'orders.detail', params: { id: lastChange.orderId } })"
        >
          <p class="mb-0">
            <span v-html="lastChangeText"></span>
            <span class="link--white"> {{ $vuetify.lang.t('$vuetify.basic.goToOrderDetail') }}.</span>
          </p>
        </v-alert>
      </template>
      <template v-slot:actions>
        <v-text-field
          :label="$vuetify.lang.t('$vuetify.search.generic')"
          outlined
          single-line
          hide-details
          :value="searchQuery"
          @input="checkSearchQuery($event)"
          @click:clear="clear"
          clearable
          :disabled="filterActive"
        />
        <div class="d-flex mt-4 w-100">
          <v-btn class="mr-4 w-50" @click="_showFilter()" :color="filterActive ? '' : 'primary'" height="56">
            <v-icon v-if="filterActive">$vuetify.icons.clear</v-icon>
            <v-icon v-else>$vuetify.icons.filter</v-icon>
          </v-btn>
          <QrCodeReaderComponent width="100%" height="56" class="w-50" />
        </div>
      </template>
    </header-component>

    <filter-component v-if="filterActive" @getItems="getFilteredItems($event)"></filter-component>

    <v-card class="mt-3">
      <v-data-table
        :headers="headers"
        :items="items"
        item-key="id"
        :loading="status === REQUEST_STATUS.LOADING"
        :server-items-length="total"
        :options.sync="options"
        :footer-props="{ 'items-per-page-options': [10, 20, 50, 100, 500], options, pagination }"
        @click:row="_redirect"
      >
        <template v-slot:item.created_at="{ item }">
          {{ item.createdAtFormatted }}
        </template>

        <template v-slot:item.customer_name="{ item }">
          <v-icon v-if="item.customerId" left>$vuetify.icons.user</v-icon>
          {{ item.customerName }}
        </template>

        <template v-slot:item.order_state_id="{ item }">
          <v-btn small :color="item.state.color" :style="{ color: item.state.textColor }" class="font-weight-bold">
            {{ item.state.label[formLanguage] }}
          </v-btn>
        </template>

        <template v-slot:item.payment_method_id="{ item }">
          {{ item.payment ? item.payment.name[formLanguage] : '' }}
        </template>

        <template v-slot:item.shipping_method_id="{ item }">
          <template v-if="item.shipping">
            <div>{{ item.shipping.name[formLanguage] }}</div>
            <div v-if="item.shipping.shippingOptionName" class="note">
              {{ item.shipping.shippingOptionName }}
            </div>
          </template>
          <div v-else>-</div>
        </template>

        <template v-slot:item.invoices_count="{ item }">
          {{ item.invoices.length }}
        </template>

        <template v-slot:item.taxed_price="{ item }">
          <div>
            {{ item.taxedPrice ? item.taxedPrice.formatted : '-' }}
          </div>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { OrderModel } from '@/common/models/order.model';
import TableStateManager from '../../common/helpers/tableStateManager';
import HeaderComponent from '../../components/layout/HeaderComponent.vue';
import FilterComponent from '../../components/orders/FilterComponent.vue';
import QrCodeReaderComponent from '../../components/global/QrCodeReaderComponent.vue';
import REQUEST_STATUS from '../../common/enums/requestStatus';
import RESOURCES from '../../common/enums/resourceEnum';

export default {
  name: 'OrdersList',

  components: {
    HeaderComponent,
    FilterComponent,
    QrCodeReaderComponent,
  },

  data() {
    return {
      REQUEST_STATUS,
      resource: RESOURCES.ORDERS,
      searchTimer: null,
      searchQuery: null,
      searchPending: false,
      filterActive: false,
      options: { sortDesc: [false], sortBy: ['created_at'] },
      headers: [
        {
          text: this.$vuetify.lang.t('$vuetify.dataTable.number'),
          align: 'left',
          value: OrderModel.ATTR_ID,
          sortable: false,
        },
        {
          text: this.$vuetify.lang.t('$vuetify.dataTable.createdAt'),
          align: 'left',
          value: OrderModel.ATTR_CREATED_AT,
        },
        {
          text: this.$vuetify.lang.t('$vuetify.dataTable.customerName'),
          align: 'left',
          value: OrderModel.EMBED_CUSTOMER_NAME,
        },
        {
          text: this.$vuetify.lang.t('$vuetify.dataTable.orderState'),
          align: 'left',
          value: OrderModel.ATTR_ORDER_STATE_ID,
        },
        {
          text: this.$vuetify.lang.t('$vuetify.dataTable.paymentMethod'),
          align: 'left',
          value: 'payment_method_id',
        },
        {
          text: this.$vuetify.lang.t('$vuetify.dataTable.shippingMethod'),
          align: 'left',
          value: 'shipping_method_id',
        },
        {
          text: this.$vuetify.lang.t('$vuetify.dataTable.invoicesCount'),
          align: 'left',
          value: 'invoices_count',
        },
        {
          text: this.$vuetify.lang.t('$vuetify.dataTable.price'),
          align: 'left',
          value: OrderModel.ATTR_TAXED_PRICE,
        },
      ],
    };
  },

  computed: {
    ...mapGetters({
      items: 'orders/getAllOrders',
      total: 'orders/getTotal',
      status: 'orders/getStatus',
      lastChange: 'orders/getLastChange',
      formLanguage: 'languages/formLanguage',
    }),

    pagination() {
      return {
        page: this.options.page,
        itemsPerPage: this.options.itemsPerPage,
        pageStart: this.options.page * this.options.itemsPerPage - this.options.itemsPerPage,
        pageStop: this.options.page * this.options.itemsPerPage,
        pageCount: Math.ceil(this.total / this.options.itemsPerPage),
        itemsLength: this.$store.state.orders.total,
      };
    },

    lastChangeText() {
      if (!this.lastChange) {
        return '';
      }

      return this.$vuetify.lang.t(
        '$vuetify.messages.latestOrderChange',
        this.lastChange.orderId,
        this.lastChange.itemsCount,
        this.lastChange.totalTaxedPrice
      );
    },
  },

  watch: {
    options: {
      deep: true,
      handler() {
        const tableState = TableStateManager.getTableState(this.resource);

        TableStateManager.setTableState(this.resource, {
          ...tableState,
          searchQuery: this.searchQuery,
          options: this.options,
        });

        if (this.options.itemsPerPage && !this.searchPending) {
          this.fetchAll({
            options: this.options,
            searchQuery: this.searchQuery,
            filterData: tableState.filterQuery,
          });
        }
      },
    },

    searchQuery() {
      const tableState = TableStateManager.getTableState(this.resource);

      TableStateManager.setTableState(this.resource, {
        ...tableState,
        options: this.options,
        searchQuery: this.searchQuery,
      });
      this.search();
    },
  },

  methods: {
    ...mapActions({
      fetchAll: 'orders/fetchAllOrders',
      update: 'orders/updateOrder',
      delete: 'orders/deleteOrder',
      setLastChange: 'orders/setLastChange',
    }),

    checkSearchQuery(event) {
      const searchQuery = TableStateManager.getTableState(this.resource, 'searchQuery');
      if (searchQuery !== event) {
        this.searchQuery = event;
        this.$set(this.options, 'page', 1);
      }
    },

    search() {
      this.selectedOrders = [];
      clearTimeout(this.searchTimer);

      this.searchTimer = setTimeout(() => {
        if (!this.searchPending) {
          this.fetchAll({ options: this.options, searchQuery: this.searchQuery });
        }
      }, 500);
    },

    clear() {
      this.selectedOrders = [];
      this.searchQuery = null;
      this.fetchAll({ options: this.options, searchQuery: this.searchQuery });
    },

    async getFilteredItems(filters) {
      this.searchPending = true;

      this.$set(this.options, 'page', 1);
      await this.fetchAll({
        options: this.options,
        filterData: filters,
      });

      this.searchPending = false;
    },

    _showFilter() {
      this.filterActive = !this.filterActive;
    },

    _redirect(event) {
      this.setLastChange(null);
      this.$router.push({ name: 'orders.detail', params: { id: event.id } });
    },
  },

  created() {
    const { options, filterQuery, searchQuery } = TableStateManager.getTableState(this.resource);
    this.options = {
      sortDesc: [false],
      sortBy: [OrderModel.ATTR_CREATED_AT],
      ...options,
    };
    const moreFieldThanPriceAndDate = 2;

    if (
      filterQuery &&
      (Object.keys(filterQuery).length > moreFieldThanPriceAndDate ||
        Object.keys(filterQuery.price).length ||
        Object.keys(filterQuery.createdAt).length)
    ) {
      this.filterActive = true;
    } else {
      this.searchQuery = searchQuery;
    }
  },
};
</script>
